import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../../components/ui/Kit';
import Spinner from '../../components/ui/Spinner';
import { api } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { format } from 'date-fns';
import { launchStepMeta, pickActiveLaunch } from './pipelineUtils';

const { FiCheck, FiLock, FiArrowRight, FiImage, FiUsers, FiCalendar, FiMessageCircle } = FiIcons;

function deriveJourney({ hasLaunch, completedCount, isLicensed, cftProgress }) {
  const onboardedDone = hasLaunch;
  const academyUnlocked = completedCount >= 3 && isLicensed;
  const cftGraduated = Boolean(cftProgress?.graduated_at);
  const cftStarted = Boolean(cftProgress);

  let currentKey = 'onboarded';
  if (!onboardedDone) currentKey = 'onboarded';
  else if (!academyUnlocked) currentKey = 'launch';
  else if (cftGraduated || cftStarted) currentKey = 'cft';
  else currentKey = 'academy';

  const stages = [
    {
      key: 'onboarded',
      label: 'Onboarded',
      sub: onboardedDone ? 'Profile started' : 'Create your first launch',
      state: onboardedDone ? 'done' : 'current',
    },
    {
      key: 'launch',
      label: 'Business Launch',
      sub: `${Math.min(completedCount, 3)} of 3 launches complete`,
      state: !onboardedDone ? 'upcoming' : academyUnlocked ? 'done' : 'current',
    },
    {
      key: 'academy',
      label: 'Prosperity Academy',
      sub: academyUnlocked ? 'Licensed · 3 launches complete' : 'Unlocks after launch #3 + license',
      state: !academyUnlocked ? 'upcoming' : currentKey === 'academy' ? 'current' : 'done',
    },
    {
      key: 'cft',
      label: 'CFT Path',
      sub: cftGraduated ? 'Graduated' : cftStarted ? 'In training' : 'Field Trainer → Certified',
      state: cftGraduated ? 'done' : currentKey === 'cft' ? 'current' : 'upcoming',
    },
  ];

  const current =
    stages.find((s) => s.state === 'current') ||
    (cftGraduated ? stages[3] : stages[0]);
  const doneCount = stages.filter((s) => s.state === 'done').length;
  const fillPct = cftGraduated ? 100 : Math.min(100, Math.round((doneCount / 3) * 100));

  return { stages, current, fillPct };
}

function TrailDot({ stage, index }) {
  if (stage.state === 'done') {
    return (
      <div className="w-10 h-10 rounded-full bg-primary border-[3px] border-primary text-white flex items-center justify-center">
        <SafeIcon icon={FiCheck} className="h-4 w-4" />
      </div>
    );
  }
  if (stage.state === 'current') {
    return (
      <div className="w-10 h-10 rounded-full bg-amber border-[3px] border-amber text-white flex items-center justify-center font-heading font-extrabold text-sm shadow-[0_0_0_5px_rgba(214,138,44,0.25)]">
        {index}
      </div>
    );
  }
  return (
    <div className="w-10 h-10 rounded-full bg-navy border-[3px] border-[#1a3b73] text-[#5b7aad] flex items-center justify-center font-heading font-extrabold text-sm">
      {index}
    </div>
  );
}

function CheckTag({ status }) {
  if (status === 'done') {
    return <span className="text-[10px] font-bold uppercase tracking-wide px-2.5 py-0.5 rounded-[100px] bg-successBg text-success">Done</span>;
  }
  if (status === 'current') {
    return <span className="text-[10px] font-bold uppercase tracking-wide px-2.5 py-0.5 rounded-[100px] bg-warningBg text-warning">Up Next</span>;
  }
  return <span className="text-[10px] font-bold uppercase tracking-wide px-2.5 py-0.5 rounded-[100px] bg-lockedBg text-locked">Locked</span>;
}

export default function PipelineDashboardHome() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [launches, setLaunches] = useState([]);
  const [activeLaunch, setActiveLaunch] = useState(null);
  const [completedCount, setCompletedCount] = useState(0);
  const [top25Count, setTop25Count] = useState(0);
  const [inviteCount, setInviteCount] = useState(0);
  const [cftProgress, setCftProgress] = useState(null);

  const load = useCallback(async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [launchRows, completed, counts, invites, cft] = await Promise.all([
        api.businessLaunches.getAllForAssociate(user.id),
        api.businessLaunches.getCompletedCountForAssociate(user.id),
        api.top25Contacts.getCategoryCounts(user.id),
        api.pipelineEvents.getForAssociate(user.id, 'free_summary_class'),
        api.cftProgress.getOwnProgress(user.id),
      ]);
      const list = launchRows || [];
      setLaunches(list);
      setCompletedCount(completed ?? 0);
      setTop25Count(Object.values(counts || {}).reduce((sum, n) => sum + Number(n || 0), 0));
      setInviteCount((invites || []).reduce((sum, ev) => sum + Number(ev.registration_count || 0), 0));
      setCftProgress(cft ?? null);

      const inProgress = pickActiveLaunch(list);
      if (inProgress) {
        const full = await api.businessLaunches.getById(inProgress.id);
        setActiveLaunch(full);
      } else {
        setActiveLaunch(null);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    load();
  }, [load]);

  const journey = useMemo(
    () =>
      deriveJourney({
        hasLaunch: (launches || []).length > 0,
        completedCount,
        isLicensed: user?.is_licensed === true,
        cftProgress,
      }),
    [launches, completedCount, user?.is_licensed, cftProgress],
  );

  const steps = (activeLaunch?.launch_steps || []).slice().sort(
    (a, b) => (a.step_number ?? 0) - (b.step_number ?? 0),
  );

  const firstName = user?.full_name?.split(' ')[0] || 'there';
  const remaining = Math.max(0, 3 - completedCount);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 gap-3">
        <Spinner size="lg" className="text-primary" />
        <p className="text-slateText text-sm">Loading your path…</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-heading font-extrabold text-navy tracking-tight">
            Welcome back, <span className="text-primary">{firstName}</span>
          </h1>
          <p className="text-slateText text-sm mt-1">Here&apos;s where you are in the system, and what&apos;s next.</p>
        </div>
        <div className="self-start rounded-[100px] border border-lineGray bg-white px-3.5 py-2 text-xs font-semibold text-slateText">
          {format(new Date(), 'EEEE, MMMM d')}
        </div>
      </div>

      <div className="relative overflow-hidden rounded-[18px] bg-navy text-white px-6 py-7 md:px-8">
        <div
          className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(0,170,255,0.18), transparent 70%)' }}
        />
        <div className="relative flex flex-col gap-3 sm:flex-row sm:items-baseline sm:justify-between mb-7">
          <div>
            <p className="text-xs font-heading font-bold uppercase tracking-[1.6px] text-skyTint">Your Path</p>
            <p className="text-sm text-[#9fb6dd] mt-1">From New Associate to Certified Field Trainer</p>
          </div>
          <p className="text-sm font-bold text-amber">Currently in: {journey.current.label}</p>
        </div>
        <div className="relative flex items-start justify-between gap-2">
          <div className="absolute top-5 left-6 right-6 h-[3px] rounded-full bg-[#1a3b73] overflow-hidden">
            <div
              className="h-full rounded-full"
              style={{
                width: `${journey.fillPct}%`,
                background: 'linear-gradient(90deg, #00AAFF, #D68A2C)',
              }}
            />
          </div>
          {journey.stages.map((stage, i) => (
            <div key={stage.key} className="relative z-[1] flex flex-1 flex-col items-center gap-2.5 min-w-0">
              <TrailDot stage={stage} index={i} />
              <p className="text-xs font-bold text-center text-white leading-tight">{stage.label}</p>
              <p className="text-[10.8px] text-[#8fa8d4] text-center leading-snug hidden sm:block">{stage.sub}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
        <div className="rounded-xl bg-skyTint px-4 py-4">
          <p className="text-2xl font-heading font-extrabold text-navy tabular-nums">{completedCount} / 3</p>
          <p className="text-[11.5px] font-semibold text-slateText mt-0.5">Business Launches complete</p>
        </div>
        <div className="rounded-xl bg-skyTint px-4 py-4">
          <p className="text-2xl font-heading font-extrabold text-navy tabular-nums">{top25Count}</p>
          <p className="text-[11.5px] font-semibold text-slateText mt-0.5">Contacts on your Top 25 list</p>
        </div>
        <div className="rounded-xl bg-warningBg px-4 py-4">
          <p className="text-2xl font-heading font-extrabold text-navy tabular-nums">{inviteCount}</p>
          <p className="text-[11.5px] font-semibold text-slateText mt-0.5">Academy invites sent</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1.3fr_1fr] gap-5">
        <div className="space-y-5">
          <Card className="p-6">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <p className="text-[10.5px] font-heading font-bold uppercase tracking-[1.3px] text-primary mb-1">System 1</p>
                <h2 className="text-[15.5px] font-heading font-extrabold text-navy">Business Launch Checklist</h2>
              </div>
              <Link to="/launch-toolkit" className="text-xs font-semibold text-primary whitespace-nowrap hover:underline">
                View toolkit →
              </Link>
            </div>
            {steps.length === 0 ? (
              <p className="text-sm text-slateText py-4">
                No launch in progress.{' '}
                <Link to="/launch-toolkit" className="text-primary font-semibold hover:underline">
                  Open the Launch Toolkit
                </Link>{' '}
                to start one.
              </p>
            ) : (
              <ul className="divide-y divide-transparent">
                {steps.map((step) => {
                  const meta = launchStepMeta(step.step_type);
                  return (
                    <li key={step.id} className="flex items-center gap-3 py-2.5 px-1.5 rounded-[10px] hover:bg-ivory">
                      <div
                        className={`w-[22px] h-[22px] rounded-full shrink-0 flex items-center justify-center border-2 ${
                          step.status === 'done'
                            ? 'bg-primary border-primary'
                            : step.status === 'current'
                              ? 'border-amber bg-warningBg'
                              : 'border-lineGray bg-white'
                        }`}
                      >
                        {step.status === 'done' && <SafeIcon icon={FiCheck} className="h-3 w-3 text-white" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[13.5px] font-semibold text-navy truncate">
                          {meta.step_number}. {meta.title}
                        </p>
                        <p className="text-[11.8px] text-slateText">{meta.description}</p>
                      </div>
                      <CheckTag status={step.status} />
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>

          <Card className="p-6">
            <p className="text-[10.5px] font-heading font-bold uppercase tracking-[1.3px] text-primary mb-1">Do This Next</p>
            <h2 className="text-[15.5px] font-heading font-extrabold text-navy mb-4">Launch Tools</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { to: '/launch-toolkit', icon: FiImage, name: 'Flyer Builder', desc: 'Generate your Zoom launch flyer' },
                { to: '/top25', icon: FiUsers, name: 'Top 25 Builder', desc: 'Guided guest list, 5 categories' },
                { to: '/launch-toolkit', icon: FiCalendar, name: 'Launch Toolkit', desc: 'Work the 5-step launch sequence' },
                { to: '/academy-hub', icon: FiMessageCircle, name: 'Academy Invites', desc: 'Invite a family to the summary class' },
              ].map((tool) => (
                <Link
                  key={tool.name}
                  to={tool.to}
                  className="rounded-xl border border-lineGray p-4 flex flex-col gap-2 hover:border-primary hover:bg-[#f7fbff] transition-colors"
                >
                  <div className="w-[30px] h-[30px] rounded-lg bg-skyTint flex items-center justify-center">
                    <SafeIcon icon={tool.icon} className="h-4 w-4 text-navy" />
                  </div>
                  <p className="text-[12.8px] font-bold text-navy">{tool.name}</p>
                  <p className="text-[11px] text-slateText leading-snug">{tool.desc}</p>
                </Link>
              ))}
            </div>
          </Card>

          {journey.current.key !== 'cft' && (
            <div className="rounded-2xl border-[1.5px] border-dashed border-lineGray px-6 py-5 flex items-center gap-4">
              <div className="w-[38px] h-[38px] rounded-[10px] bg-ivory flex items-center justify-center shrink-0">
                <SafeIcon icon={FiLock} className="h-[18px] w-[18px] text-slateText" />
              </div>
              <div>
                <p className="text-[13.5px] font-bold text-navy">CFT Path — locked</p>
                <p className="text-[11.8px] text-slateText mt-0.5">
                  {remaining > 0
                    ? `Complete ${remaining} more Business Launch${remaining === 1 ? '' : 'es'} and get licensed to unlock the Field Trainer path.`
                    : user?.is_licensed
                      ? 'Your CFT path will appear here once a trainer starts your progress.'
                      : 'Get licensed to unlock the Field Trainer path.'}
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-5">
          <Card className="p-6">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <p className="text-[10.5px] font-heading font-bold uppercase tracking-[1.3px] text-primary mb-1">System 2</p>
                <h2 className="text-[15.5px] font-heading font-extrabold text-navy">Prosperity Academy</h2>
              </div>
              <Link to="/academy-hub" className="text-xs font-semibold text-primary whitespace-nowrap hover:underline inline-flex items-center gap-1">
                Open hub <SafeIcon icon={FiArrowRight} className="h-3 w-3" />
              </Link>
            </div>
            <div className="space-y-2.5">
              {[
                ['01', 'Cashflow', 'Budgeting, goal setting, debt & credit'],
                ['02', 'Risk Management', 'Term, Whole Life, IUL, Final Expense'],
                ['03', 'Wealth Building', 'Infinite Banking, LIRP, FIA'],
                ['04', 'Legacy Planning', 'Wills, trusts, generational wealth'],
              ].map(([num, name, desc]) => (
                <div key={num} className="flex items-center gap-3 py-1">
                  <div className="w-[26px] h-[26px] rounded-[7px] bg-navy text-white text-[11.5px] font-extrabold flex items-center justify-center shrink-0">
                    {num}
                  </div>
                  <div>
                    <p className="text-[13px] font-bold text-navy">{name}</p>
                    <p className="text-[11.3px] text-slateText">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
